import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';

import { FixturesService } from '../../services/fixtures.service';
import { EventsService } from '../../services/events.service';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'app-fixtures',
  templateUrl: './fixtures.component.html',
  styleUrls: ['./fixtures.component.scss']
})
export class FixturesComponent implements OnInit {

  loading: boolean;
  fixtureDate: {
    year: number,
    month: number,
    day: number
  };
  fixturesList: any[];

  constructor(
    private router: Router,
    private notification: ToastrService,
    private fixturesService: FixturesService,
    private eventsService: EventsService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    // Check if user is authenticated before making API calls
    const authUser = this.authService.getFromSession();
    if (!authUser || !authUser.roles || !authUser.roles.length) {
      this.router.navigate(['/login']);
      return;
    }

    const today = moment();
    this.fixtureDate = {
      year: today.year(),
      month: today.month() + 1,
      day: today.date()
    };

    this.getFixtures(this.fixtureDate);
  }

  // Force datepicker to close if click outside of it
  datepickerFix = (event, datePicker) => {
    if (event.target.offsetParent == null) {
      datePicker.close();
    } else if (event.target.offsetParent.nodeName !== 'NGB-DATEPICKER') {
      datePicker.close();
    }
  }

  getFixtures = (date: { year, month, day }): void => {
    this.loading = true;
    const requestDate = Object.assign({}, date);
    requestDate.month -= 1;
    this.fixturesService.getFixtures(moment(requestDate).format('YYYYMMDD'))
    .subscribe(
      res => {
        this.fixturesList = res.map(f => ({
          racesLink: `/races/${moment(f.fixtureDate).year()}/${f.fixtureId}`,
          courseName: f.courseName,
          abandonedReasonCode: f.abandonedReasonCode,
          abandonedReason: f.abandonedReason,
          fixtureType: f.fixtureType,
          racingTrackType: f.racingTrackType,
          trotUpsRequired: Number(f.trotUpsRequired) === 1,
          isPremier: f.highlightDescription === 'Premier',
          highlightDescription: f.highlightDescription,
          firstRaceTime: f.firstRaceTime,
          lastRaceTime: f.lastRaceTime,
          numberOfRaces: f.numberOfRaces,
          numberOfRunners: f.numberOfRunners,
          musicDescription: f.musicDescription,
          lowSunProvision: f.lowSunProvision,
          sunsetTime: f.sunsetTime,
          floodlit: f.floodlit === true || f.floodlit === 1 || f.floodlit === '1' || f.floodlit === 'Y',
          fixtureSession: f.fixtureSession,
          fixtureDate: f.fixtureDate,
          overnightDate: f.overnightDate
        }));
        // console.log('Fixtures List', this.fixturesList);
        this.loading = false;
      },
      err => {
        console.error(err);
        this.loading = false;
        if (err.status === 401) {
          // Don't show error toast for 401 - user will be redirected to login
          this.router.navigate(['/logout']);
        } else {
          this.notification.error(`Could't get the Fixtures List: ${this.eventsService.getApiErrorMessage(err)}`, 'Error!');
        }
      }
    );
  }

  canOpenFixture(fixture: { numberOfRunners?: number }): boolean {
    return Number(fixture.numberOfRunners) > 0;
  }

  abandonedLabel(fixture: {
    abandonedReasonCode?: number | string;
    abandonedReason?: string;
  }): string {
    if (!fixture.abandonedReasonCode) {
      return null;
    }
    if (Number(fixture.abandonedReasonCode) === 1) {
      return 'Abandoned';
    }
    const reason = (fixture.abandonedReason || '').trim();
    return reason ? `Abandoned - ${reason}` : 'Abandoned';
  }

  fixtureCardClasses(fixture: {
    abandonedReasonCode?: string;
    floodlit?: boolean;
    fixtureSession?: string;
  }): { [key: string]: boolean } {
    return {
      'fixture-abandoned': !!fixture.abandonedReasonCode,
      'session-afternoon': !fixture.floodlit && fixture.fixtureSession === 'AFTERNOON',
      'session-evening': !fixture.floodlit && fixture.fixtureSession === 'EVENING',
      'session-floodlit': !!fixture.floodlit
    };
  }

  sessionLabel(fixture: { fixtureSession?: string; floodlit?: boolean }): string {
    if (fixture.floodlit) {
      return 'Floodlit';
    }
    if (fixture.fixtureSession === 'AFTERNOON') {
      return 'Afternoon';
    }
    if (fixture.fixtureSession === 'EVENING') {
      return 'Evening';
    }
    return null;
  }

  showSunsetRow(fixture: {
    lowSunProvision?: string;
    sunsetTime?: string;
    fixtureSession?: string;
    floodlit?: boolean;
  }): boolean {
    if (fixture.lowSunProvision) {
      return true;
    }
    const isDayOrEveningSession =
      fixture.fixtureSession === 'AFTERNOON' || fixture.fixtureSession === 'EVENING';
    return !!(fixture.sunsetTime && isDayOrEveningSession && !fixture.floodlit);
  }

  /**
   * Thresholds from fixtureDate / overnightDate only (wall-clock now):
   * - before overnightDate 14:00 → no badge
   * - from overnightDate 14:00 until (fixtureDate − 1 day) 15:20 → DC
   * - from (fixtureDate − 1 day) 15:20 → RP
   */
  privilegeBadge(fixture: {
    overnightDate?: string;
    fixtureDate?: string;
    numberOfRunners?: number;
  }): 'DC' | 'RP' | null {
    if (!this.canOpenFixture(fixture)) {
      return null;
    }
    if (!fixture.overnightDate || !fixture.fixtureDate) {
      return null;
    }

    const now = moment();
    const dcStart = moment(fixture.overnightDate).hour(14).minute(0).second(0).millisecond(0);
    const rpStart = moment(fixture.fixtureDate).subtract(1, 'day').hour(15).minute(20).second(0).millisecond(0);

    if (now.isBefore(dcStart)) {
      return null;
    }
    if (!now.isBefore(rpStart)) {
      return 'RP';
    }
    return 'DC';
  }

  goSearch() {
    this.router.navigate(['/search']);
  }
}
